import { useState } from 'react';

import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import type { ScrapedWatchEntry } from '../CuratedWatches/BulkImport';
import {
  fetchSquareProducts,
  resolveSquareScope,
  squareProductsToEntries,
} from '../CuratedWatches/SquareOnlineScraper';
import {
  mergePastedEntries,
  parsePastedSquare,
  parsePastedWooProducts,
  pastedWooToEntries,
  variableProductsMissingVariations,
  wooPasteUrl,
  wooVariationsPasteUrl,
} from '../CuratedWatches/storeJsonPaste';
import { wooHost } from '../CuratedWatches/storeDocIds';
import type { WooProduct } from '../CuratedWatches/WooCommerceScraper';
import type { SquareOnlineImportSource, WooCommerceImportSource } from '../../types';

export interface PasteStoreJsonModalProps {
  source: WooCommerceImportSource | SquareOnlineImportSource;
  /** Why the modal opened (the blocked-request message), shown at the top. */
  reason?: string | null;
  /** Fired with every product gathered across the pasted pages. */
  onDone: (entries: ScrapedWatchEntry[]) => void;
  onCancel: () => void;
}

type Entry = ScrapedWatchEntry & { wooProductId?: string; squareProductId?: string };

const kbd = 'px-1 py-0.5 bg-gray-100 rounded text-xs font-mono';

/**
 * Manual "paste the JSON" fallback for the WooCommerce and Square Online
 * sources — the store-API counterpart of PasteHtmlModal (same layout: steps, an
 * Open ↗ button for the exact address, a textarea, Parse). For shops that
 * refuse every automated request: the curator's own browser can still read
 * them. Parsing/validation: CuratedWatches/storeJsonPaste.ts.
 */
export function PasteStoreJsonModal({ source, reason, onDone, onCancel }: PasteStoreJsonModalProps) {
  const isWoo = source.kind === 'woocommerce';
  const expand = source.expandVariants !== false;
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Square: entries as they come. WooCommerce: the raw products and
  // variations, so variable products expand once their variations are in.
  const [squareEntries, setSquareEntries] = useState<Entry[]>([]);
  const [wooProducts, setWooProducts] = useState<WooProduct[]>([]);
  const [wooVariations, setWooVariations] = useState<WooProduct[]>([]);
  const [page, setPage] = useState(1);
  const [variationPage, setVariationPage] = useState(1);
  const [moreHint, setMoreHint] = useState<string | null>(null);

  let host = '';
  try {
    host = wooHost(source.categoryUrl);
  } catch {
    // shown as a parse error on Parse
  }
  const entries: Entry[] = isWoo ? pastedWooToEntries(wooProducts, wooVariations, host, expand) : squareEntries;
  const missingVariations = isWoo && expand ? variableProductsMissingVariations(wooProducts, wooVariations) : 0;

  const openUrl = isWoo ? wooPasteUrl(source.categoryUrl, page) : source.categoryUrl.trim();
  const variationsUrl = isWoo ? wooVariationsPasteUrl(source.categoryUrl, variationPage) : null;

  const accept = (next: Entry[], hint: string | null) => {
    setSquareEntries((prev) => mergePastedEntries(prev, next));
    setMoreHint(hint);
    setText('');
    setError(null);
  };

  const byId = (prev: WooProduct[], next: WooProduct[]) => {
    const seen = new Set(prev.map((p) => p.id));
    return [...prev, ...next.filter((p) => !seen.has(p.id))];
  };

  const handleParse = async () => {
    setError(null);
    if (isWoo) {
      const r = parsePastedWooProducts(text, source.categoryUrl);
      if (!r.ok) return setError(r.error);
      const { products, variations, maybeMore } = r.value;
      const isVariationPage = variations.length > 0 && products.length === 0;
      setWooProducts((prev) => byId(prev, products));
      setWooVariations((prev) => byId(prev, variations));
      if (maybeMore) (isVariationPage ? setVariationPage : setPage)((p) => p + 1);
      setText('');
      setError(null);
      setMoreHint(
        maybeMore
          ? `That page was full (100 ${isVariationPage ? 'variations' : 'products'}) — there may be more. Open the next page ↗ and paste it too, or import what you have.`
          : null,
      );
      return;
    }
    const r = parsePastedSquare(text, expand);
    if (!r.ok) return setError(r.error);
    if (r.value.kind === 'products') {
      const { currentPage, totalPages } = r.value;
      accept(
        r.value.entries,
        currentPage < totalPages
          ? `That was page ${currentPage} of ${totalPages}. Open the same address with page=${currentPage + 1} and paste it too, or import what you have.`
          : null,
      );
      return;
    }
    // Storefront page source: we now have the store ids, and the catalogue
    // API itself is readable from the browser — fetch every page directly.
    const { store } = r.value;
    const scope = resolveSquareScope(source.categoryUrl, store);
    if ('error' in scope) return setError(scope.error);
    setBusy(true);
    try {
      const products = await fetchSquareProducts(store, scope.category?.siteCategoryId);
      if (products.length === 0) return setError('The store has no visible products for this URL.');
      accept(squareProductsToEntries(products, store.siteId, expand), null);
    } catch (err) {
      setError(
        `Read the store, but its product catalogue could not be fetched (${err instanceof Error ? err.message : String(err)}). Paste the products JSON instead.`,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onCancel} title={isWoo ? 'Paste Products JSON' : 'Paste Storefront Source'}>
      <div className="space-y-3">
        {reason && (
          <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{reason}</p>
        )}
        <div className="flex items-start gap-2">
          <div className="flex-1 space-y-2">
            <p className="text-sm text-gray-600">
              This shop refuses our automated requests, but your own browser can still read it.
            </p>
            {isWoo ? (
              <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
                <li>
                  Open the address below (<span className="font-medium">Open JSON ↗</span>) — your browser
                  shows the shop&rsquo;s product list as text.
                </li>
                <li>
                  Press <kbd className={kbd}>Cmd+A</kbd> <kbd className={kbd}>Cmd+C</kbd> to copy all of it
                  (in Firefox, use the <span className="font-medium">Raw Data</span> tab first).
                </li>
                <li>Paste into the box below and hit Parse.</li>
              </ol>
            ) : (
              <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
                <li>
                  Open the shop page below (<span className="font-medium">Open page ↗</span>) and let it
                  load.
                </li>
                <li>
                  Press <kbd className={kbd}>Cmd+Option+U</kbd> to view source, then{' '}
                  <kbd className={kbd}>Cmd+A</kbd> <kbd className={kbd}>Cmd+C</kbd>.
                </li>
                <li>
                  Paste below and hit Parse — the store&rsquo;s products are then fetched for you. (A
                  products API response can be pasted here too.)
                </li>
              </ol>
            )}
          </div>
          {openUrl && (
            <button
              type="button"
              className="shrink-0 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
              onClick={() => window.open(openUrl, '_blank')}
            >
              {isWoo ? (page > 1 ? `Open page ${page} ↗` : 'Open JSON ↗') : 'Open page ↗'}
            </button>
          )}
        </div>
        {openUrl && (
          <div className="rounded bg-gray-50 px-2 py-1">
            <span className="break-all font-mono text-xs text-gray-500">{openUrl}</span>
          </div>
        )}
        {moreHint && <p className="text-sm text-blue-700">{moreHint}</p>}
        {missingVariations > 0 && variationsUrl && (
          <div className="flex items-start justify-between gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
            <span>
              {missingVariations} product{missingVariations === 1 ? ' has' : 's have'} options. To import each
              option as its own reference, also open the shop&rsquo;s variations list and paste it here (or
              import now and {missingVariations === 1 ? 'it imports' : 'they import'} as one reference each).
            </span>
            <button
              type="button"
              className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
              onClick={() => window.open(variationsUrl, '_blank')}
            >
              Open variations ↗
            </button>
          </div>
        )}
        <textarea
          aria-label={isWoo ? 'Paste raw products JSON' : 'Paste storefront page source or products JSON'}
          className="h-56 w-full rounded-lg border p-2 font-mono text-xs"
          placeholder={isWoo ? 'Paste raw products JSON here…' : 'Paste the page source (or products JSON) here…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {entries.length > 0
              ? `${entries.length} product${entries.length === 1 ? '' : 's'} ready`
              : text.length > 0
                ? `${text.length.toLocaleString()} chars`
                : ''}
          </span>
          <div className="flex gap-2">
            <button type="button" className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700" onClick={onCancel}>
              Cancel
            </button>
            <Button variant="secondary" onClick={() => void handleParse()} disabled={!text.trim() || busy}>
              {busy ? 'Fetching…' : 'Parse'}
            </Button>
            <Button onClick={() => onDone(entries)} disabled={entries.length === 0 || busy}>
              Import {entries.length > 0 ? entries.length : ''} products
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
